import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  Button,
  DataTable,
  Font,
  Grid,
  LoadingSpinner,
  Pagination,
  Search,
  Icon,
} from "pepsico-ds";
import AddEditUser from "./AddEditUser/AddEditUser";
import { useAppDispatch, useAppSelector } from "../../../hooks/app";
import { getUsers } from "../../../redux/actions/users/userAction";
import {
  getRoles,
  getSectors,
} from "../../../redux/actions/appData/appDataAction";
import { User } from "../../../types/users";
import "./UserListStyle.css";
import { ROLES } from "../../../config/roles";
import { useCanManageUser } from "../../../hooks/access/canManageUser";
import { useNavigate } from "react-router-dom";

function UserList() {
  const { gpid: userGpid } = useAppSelector((state) => state.userAccess.data);
  const canManageUser = useCanManageUser();
  const navigate = useNavigate();
  useLayoutEffect(() => {
    if (canManageUser === false && userGpid) {
      navigate("/");
    }
  }, [canManageUser, navigate, userGpid]);
  const { hasAdmin, gpLeadSectors, userRoles } = useAppSelector(
    (state) => state.userAccess.data
  );
  const isGPLead = userRoles.some(
    (role) => role.role.name === ROLES.gpLead.displayText
  );
  const { gpid } = useAppSelector((state) => state.auth);
  const [editUser, setEditUser] = useState<User>();
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState("");
  const { isLoading, count, data } = useAppSelector((state) => state.users);
  const pageCount = count > 10 ? Math.ceil(count / 10) : 1;

  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isAdd, setIsAdd] = useState(true);
  const openAddUser = () => {
    setIsAdd(true);
    setShowModal(true);
  };
  const openEditUser = (user: User) => {
    setEditUser(user);
    setIsAdd(false);
    setShowModal(true);
  };
  useEffect(() => {
    setSearch("");
    setPageNumber(1);
  }, []);
  useEffect(() => {
    const promise = dispatch(getUsers({ page: pageNumber, limit: 10, search }));
    return () => {
      promise.abort();
    };
  }, [dispatch, pageNumber, search]);
  useEffect(() => {
    dispatch(getRoles({ hasAdmin }));
    dispatch(getSectors({ hasAdmin, gpLeadSectors }));
  }, [dispatch, hasAdmin, gpLeadSectors]);

  const reloadFunction = () => {
    dispatch(getUsers({ page: pageNumber, limit: 10, search }));
  };
  const columns = [
    {
      columnKey: "gpid",
      header: "GPID",
      size: 80,
      minSize: 80,
      maxSize: 80,
    },
    {
      columnKey: "firstName",
      header: "First Name",
    },
    {
      columnKey: "lastName",
      header: "Last Name",
    },
    {
      columnKey: "email",
      header: "Email",
    },
    {
      columnKey: "roles",
      header: "Roles",
      size: 90,
      minSize: 90,
      maxSize: 90,
      cellData: (ctx: any) => {
        const roles = ctx.row.original.roles;
        return (
          <div>
            {roles.split("\n").map((role: string) => (
              <div key={role}>{role}</div>
            ))}
          </div>
        );
      },
    },
    {
      columnKey: "sectors",
      header: "Sector",
      size: 90,
      minSize: 90,
      maxSize: 90,
      cellData: (ctx: any) => {
        const sectors = ctx.row.original.sectors;
        return (
          <div>
            {sectors.split("\n").map((sector: string) => (
              <div key={sector}>{sector}</div>
            ))}
          </div>
        );
      },
    },
    {
      columnKey: "id",
      header: "Action",
      cellData: (ctx: any) => {
        const user = ctx.row.original;
        if (Number(gpid) === Number(user.gpid)) {
          return <></>;
        }
        if (isGPLead) {
          const userIsAdmin = user?.userRoles.find(
            (role: any) => role.displayText === ROLES.admin.displayText
          );
          if (userIsAdmin) {
            return <></>;
          }
        }
        return (
          <Button
            style={{ color: "#A0A0A0", backgroundColor: "transparent" }}
            size={"small"}
            variant={"primaryInverse"}
            onClick={() => openEditUser(user)}
          >
            <Icon icon="edit" style={{ fontSize: 18 }} />
          </Button>
        );
      },
      size: 20,
      minSize: 20,
      maxSize: 20,
    },
  ];

  const onClear = () => {
    setSearch("");
  };
  const onSearch = (value: string) => {
    setSearch(value);
    setPageNumber(1);
  };

  return (
    <>
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 10,
            alignItems: "center",
            position: "sticky", // Make it sticky
            top: 0, // Stick to the top of the container
            backgroundColor: "#fff", 
            zIndex: 10, 
            padding: "10px 0", 
          }}
        >
          <Font size={"body"} variant={"medium"}>
            User List ({count})
          </Font>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ display: "flex", gap: 1, flexDirection: "column" }}>
              <Search isClearable onClear={onClear} onUpdate={onSearch} />
              <Font
                size={"xxsmall"}
                variant={"regular"}
                style={{ color: "#A0A0A0" }}
              >
                Search by GPID, First Name, Last Name, Email
              </Font>
            </div>
            <Button size={"medium"} onClick={openAddUser}>
              Add User
            </Button>
          </div>
        </div>

        {isLoading ? (
          <Grid
            style={{
              placeContent: "center",
              minHeight: 200,
              gridTemplateColumns: "auto",
            }}
          >
            <LoadingSpinner
              size={{
                height: "60px",
                width: "60px",
              }}
            />
          </Grid>
        ) : (
          <div>
            <div id={"user-data-table"}>
              <DataTable
                columns={columns}
                data={data}
                isExpandable={false}
                isResizable={false}
                selection={"single"}
                pageIndex={0}
                pageSize={10}
                size={"large"}
                tableDescription="Data table description"
                tableHeader="Data table header"
                showColumnFilters={false}
                enableSorting={false}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px 0px",
                }}
              >
                <Pagination
                  currentPage={pageNumber}
                  pageCount={pageCount}
                  size="medium"
                  variant="number"
                  onUpdate={(newPageNumber) => setPageNumber(newPageNumber)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <AddEditUser
        showModal={showModal}
        onCloseModal={() => setShowModal(false)}
        isAdd={isAdd}
        editUser={editUser}
        reloadFunction={reloadFunction}
      />
    </>
  );
}

export default UserList;
